import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

export interface CostTiers {
  bottomThirdCutoff: number;
  topThirdCutoff: number;
  percentiles?: {[key: number]: number}; // Percentiles 1-99
  hasFreeModels: boolean;
  freeModelIds: string[];
  lastUpdated: string;
  totalModels: number;
  paidModelsCount: number;
  freeModelsCount: number;
}

export class CostTiersService {
  /**
   * Fetch cost tier cutoffs from Firestore
   * Cost tiers are now stored in system/config document
   */
  static async getCostTiers(): Promise<CostTiers | null> {
    try {
      console.log('🔍 Fetching cost tiers from Firestore...');

      const configRef = doc(db, 'system', 'config');
      const configSnap = await getDoc(configRef);

      if (!configSnap.exists()) {
        console.warn('⚠️ System config document not found in Firestore');
        return null;
      }

      const configData = configSnap.data();
      const data = configData.costTiers as CostTiers;

      if (!data) {
        console.warn('⚠️ Cost tiers not found in system config');
        return null;
      }
      
      console.log(`📊 Retrieved cost tiers from Firestore`);
      console.log(`📅 Last updated: ${data.lastUpdated}`);
      console.log(`💰 Cutoffs: $=${data.bottomThirdCutoff}, $$=${data.topThirdCutoff}`);
      console.log(`🆓 Free models: ${data.freeModelsCount}`);

      return data;
    } catch (error) {
      console.error('❌ Error fetching cost tiers from Firestore:', error);
      throw error;
    }
  }

  /**
   * Calculate cost per million tokens using blended pricing (3:1 prompt:completion)
   * This matches the backend calculation method
   */
  static calculateCostPerMillion(promptPrice: number, completionPrice: number): number {
    // Weighted average: 3 parts prompt, 1 part completion
    const blendedPerToken = (promptPrice * 3 + completionPrice * 1) / 4;
    // Convert to cost per million tokens
    return blendedPerToken * 1_000_000;
  }

  /**
   * Determine cost tier ($, $$, $$$, or $0) for a model based on its pricing and cutoffs
   */
  static getCostTier(
    modelId: string,
    promptPrice: number | null,
    completionPrice: number | null,
    costTiers: CostTiers | null
  ): '$' | '$$' | '$$$' | '$0' {
    // Check if model is free by suffix (e.g. qwen/qwen3-4b:free)
    if (modelId.endsWith(':free')) {
      return '$0';
    }

    // Check if model is in the free models list
    if (costTiers?.freeModelIds.includes(modelId)) {
      return '$0';
    }

    // If no pricing or no cost tiers, default to $
    if (!promptPrice || !completionPrice || !costTiers) {
      return '$';
    }

    // Check if model is free (both prices are 0)
    if (promptPrice === 0 && completionPrice === 0) {
      return '$0';
    }

    // Calculate cost per million tokens
    const costPerMillion = this.calculateCostPerMillion(promptPrice, completionPrice);

    // Convert cutoffs from per-token to per-million for comparison
    const bottomCutoffPerMillion = costTiers.bottomThirdCutoff * 1_000_000;
    const topCutoffPerMillion = costTiers.topThirdCutoff * 1_000_000;

    // Determine tier based on cutoffs
    if (costPerMillion <= bottomCutoffPerMillion) {
      return '$';
    } else if (costPerMillion <= topCutoffPerMillion) {
      return '$$';
    } else {
      return '$$$';
    }
  }

  /**
   * Calculate the percentile rank (1-99) for a given cost
   * Returns the percentile where this cost falls
   */
  static getCostPercentile(
    promptPrice: number | null,
    completionPrice: number | null,
    costTiers: CostTiers | null
  ): number | null {
    // If no pricing or no cost tiers, or no percentile data, return null
    if (!promptPrice || !completionPrice || !costTiers || !costTiers.percentiles) {
      return null;
    }

    // Check if model is free (both prices are 0)
    if (promptPrice === 0 && completionPrice === 0) {
      return 0; // Free models are at 0th percentile
    }

    // Calculate cost per million tokens
    const costPerMillion = this.calculateCostPerMillion(promptPrice, completionPrice);

    // Find the percentile where this cost falls
    // Start from 99th and work down to find the first percentile below this cost
    for (let p = 99; p >= 1; p--) {
      if (costPerMillion >= costTiers.percentiles[p]) {
        return p;
      }
    }

    // If cost is below all percentiles, it's in the 1st percentile
    return 1;
  }
}

